// purchaseOrderPaymentsModel.js
//
// Field reads + paid rollups for Purchase Order Payments.

import { parsePriceCents } from 'utilities/Currency';
import { readNodeFieldRawValue } from 'utilities/NodeFieldData';
import { getEnumOptionLabel, resolveEnumOptionsForField } from 'utilities/EnumOptions';
import { formatPurchaseOrderDate } from 'nodetypes/purchaseorder/view/purchaseOrderGeneralInfoModel';
import { PURCHASE_ORDER_PAYMENT_TYPE_KEY } from 'nodetypes/purchaseorder/view/purchaseOrderPaymentFromPo';

function normalizeString(value) {
  return String(value || '').trim();
}

/**
 * @param {unknown} value
 * @returns {number|null}
 */
export function toPaymentMoneyNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const cents = parsePriceCents(value);
  return Number.isFinite(cents) ? cents : null;
}

/**
 * @param {object|null|undefined} payment
 * @param {string} fieldName
 * @returns {unknown}
 */
export function readPurchaseOrderPaymentField(payment, fieldName) {
  return readNodeFieldRawValue(
    payment,
    { name: fieldName, source: 'purchase_order_payment' },
    PURCHASE_ORDER_PAYMENT_TYPE_KEY,
  );
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isPurchaseOrderPaymentPaidStatus(status) {
  const normalized = normalizeString(status).toLowerCase();
  return normalized === 'paid';
}

/**
 * Sum of amounts for payments marked Paid.
 *
 * @param {object[]} payments
 * @returns {number}
 */
export function sumPaidPurchaseOrderPayments(payments) {
  return (Array.isArray(payments) ? payments : []).reduce((sum, payment) => {
    const status = readPurchaseOrderPaymentField(payment, 'status');
    if (!isPurchaseOrderPaymentPaidStatus(status)) return sum;
    const amount = toPaymentMoneyNumber(readPurchaseOrderPaymentField(payment, 'amount'));
    return sum + (amount == null ? 0 : amount);
  }, 0);
}

/**
 * @param {object|null|undefined} payment
 * @param {object|null|undefined} [paymentType]
 * @param {string} [language='en']
 * @returns {{
 *   key: string,
 *   title: string,
 *   amount: number|null,
 *   paymentTerms: string,
 *   paymentTermsLabel: string,
 *   dueDate: unknown,
 *   dueDateLabel: string,
 *   status: string,
 *   statusLabel: string,
 *   method: string,
 *   reference: string,
 * }}
 */
export function getPurchaseOrderPaymentRowModel(payment, paymentType = null, language = 'en') {
  const fields = Array.isArray(paymentType?.schema?.fields) ? paymentType.schema.fields : [];
  const termsField = fields.find((field) => normalizeString(field?.name) === 'paymentTerms') || null;
  const statusField = fields.find((field) => normalizeString(field?.name) === 'status') || null;

  const paymentTerms = normalizeString(readPurchaseOrderPaymentField(payment, 'paymentTerms'));
  const status = normalizeString(readPurchaseOrderPaymentField(payment, 'status'));
  const dueDate = readPurchaseOrderPaymentField(payment, 'dueDate');

  const termsOptions = termsField ? resolveEnumOptionsForField(termsField, language) : [];
  const statusOptions = statusField ? resolveEnumOptionsForField(statusField, language) : [];

  return {
    key: normalizeString(payment?.key),
    title: normalizeString(payment?.title) || 'Untitled payment',
    amount: toPaymentMoneyNumber(readPurchaseOrderPaymentField(payment, 'amount')),
    paymentTerms,
    paymentTermsLabel: paymentTerms
      ? (getEnumOptionLabel(termsOptions, paymentTerms, language) || paymentTerms)
      : '',
    dueDate,
    dueDateLabel: formatPurchaseOrderDate(dueDate),
    status,
    statusLabel: status
      ? (getEnumOptionLabel(statusOptions, status, language) || status)
      : '',
    method: normalizeString(readPurchaseOrderPaymentField(payment, 'method')),
    reference: normalizeString(readPurchaseOrderPaymentField(payment, 'reference')),
  };
}
